// src/pages/bloomterminal/components/dashboard/AddWidgetPopup.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, BarChart4, Check } from 'lucide-react';
import { getAllCharts, isChartRestricted } from '../../config/chartRegistry';
import { useUser } from '../../../context/UserContext';
import RestrictedChartOverlay from './RestrictedChartOverlay';

/**
 * AddWidgetPopup - Modal for selecting which widgets are on the dashboard
 *
 * Features:
 * - Added widgets shown first, then not-added (reordered)
 * - Search input with tag matching
 * - Click to toggle widget on/off (add or remove from selection)
 * - Save/OK applies selection and updates endpoint; close without saving discards changes
 */
const AddWidgetPopup = ({ isOpen, onClose, onSave, activeWidgetIds = [] }) => {
  const { t } = useTranslation();
  const { tier, chartRestrictions } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWidgetIds, setSelectedWidgetIds] = useState(() => [...activeWidgetIds]);

  useEffect(() => {
    if (isOpen) {
      setSelectedWidgetIds([...activeWidgetIds]);
    }
  }, [isOpen, activeWidgetIds]);

  // Get all available charts
  const allCharts = useMemo(() => getAllCharts(), []);

  // Filter charts by search query (matches translated title, description, and tags)
  const filteredCharts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return allCharts;

    return allCharts.filter((chart) => {
      const title = t(chart.titleKey).toLowerCase();
      const description = t(chart.descriptionKey).toLowerCase();
      return (
        title.includes(query) ||
        description.includes(query) ||
        chart.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    });
  }, [searchQuery, allCharts, t]);

  const sortedCharts = useMemo(() => {
    const byId = Object.fromEntries(filteredCharts.map((c) => [c.id, c]));
    const selected = selectedWidgetIds.map((id) => byId[id]).filter(Boolean);
    const notSelected = filteredCharts.filter((c) => !selectedWidgetIds.includes(c.id));
    return [...selected, ...notSelected];
  }, [filteredCharts, selectedWidgetIds]);

  // Toggle widget in selection (add or remove)
  const handleToggleWidget = (chartId) => {
    const isLocked = isChartRestricted(chartId, tier, chartRestrictions);
    const isSelected = selectedWidgetIds.includes(chartId);
    // Locked charts can't be newly selected, but can be unselected if already active.
    if (isLocked && !isSelected) return;

    setSelectedWidgetIds((prev) =>
      prev.includes(chartId) ? prev.filter((id) => id !== chartId) : [...prev, chartId]
    );
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Close without saving (discard in-modal changes)
  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  // Save selection and close
  const handleSave = () => {
    onSave(selectedWidgetIds);
    setSearchQuery('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleBackdropClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70" />

          {/* Modal */}
          <motion.div
            className="relative bg-white dark:bg-[#1a1a1a] border border-[#E8EBE9] dark:border-[#3a3c3b] rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
              <h3 className="text-[#282A29] dark:text-white text-lg font-semibold">{t('analytics.addWidgetTitle')}</h3>
              <button
                onClick={handleClose}
                className="p-1.5 rounded hover:bg-[#F0F5F2] dark:hover:bg-[#282A29] transition-colors"
              >
                <X size={20} className="text-[#828382] dark:text-gray-400 hover:text-[#282A29] dark:hover:text-white" />
              </button>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-[#E8EBE9] dark:border-[#3a3c3b]">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[#828382] dark:text-gray-500"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('analytics.searchPlaceholder')}
                  className="w-full bg-white dark:bg-gray-800 border border-[#E8EBE9] dark:border-gray-700 rounded-lg px-4 py-2.5 pl-10 text-[#282A29] dark:text-white text-sm placeholder-[#828382] dark:placeholder-gray-500 focus:outline-none focus:border-bloom-green transition-colors"
                  autoFocus
                />
              </div>
            </div>

            {/* Chart Grid */}
            <div className="flex-1 overflow-y-auto p-5">
              {sortedCharts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-[#828382] dark:text-gray-500">
                  <Search size={48} className="mb-4 opacity-50" />
                  <p>{t('analytics.noChartsFound', { query: searchQuery })}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {sortedCharts.map((chart) => {
                    const isSelected = selectedWidgetIds.includes(chart.id);
                    const isLocked = isChartRestricted(chart.id, tier, chartRestrictions);
                    const canToggle = !isLocked || isSelected;

                    return (
                      <button
                        key={chart.id}
                        onClick={() => handleToggleWidget(chart.id)}
                        disabled={!canToggle}
                        className={`
                          p-4 rounded-lg border text-left transition-all duration-200
                          ${
                            isSelected
                              ? `border-bloom-green/50 bg-bloom-green/10 ${canToggle ? 'cursor-pointer' : 'cursor-not-allowed'} hover:border-bloom-green`
                              : isLocked
                                ? 'border-[#E8EBE9] dark:border-gray-700 cursor-not-allowed'
                                : 'border-[#E8EBE9] dark:border-gray-700 hover:border-bloom-green hover:bg-[#F0F5F2] dark:hover:bg-[#222222] active:scale-[0.98] cursor-pointer'
                          }
                        `}
                      >
                        {/* Preview */}
                        <div className="relative h-40 rounded-lg bg-white dark:bg-[#191919] mb-3 flex items-center justify-center overflow-hidden border border-[#E8EBE9] dark:border-gray-800">
                          {chart.previewImage ? (
                            <img
                              src={chart.previewImage}
                              alt={t(chart.titleKey)}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <BarChart4 size={32} className="text-[#828382] dark:text-gray-600" />
                          )}

                          {isLocked && <RestrictedChartOverlay />}

                          {/* Selected indicator (top-right, works for locked too) */}
                          {isSelected && (
                            <div className="absolute top-2 right-2 z-50 bg-bloom-green rounded-full p-1 shadow-md ring-2 ring-white/80 dark:ring-black/40 pointer-events-none">
                              <Check size={16} className="text-white" />
                            </div>
                          )}
                        </div>

                        {/* Title (between preview and description) */}
                        <h4 className="text-[#282A29] dark:text-white font-medium mb-1">
                          {t(chart.titleKey)}
                        </h4>

                        {/* Description */}
                        <p className="text-[#828382] dark:text-gray-500 text-xs mb-2 line-clamp-1">
                          {t(chart.descriptionKey)}
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-1">
                          {chart.tags.slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="text-xs px-2 py-0.5 bg-[#F0F5F2] dark:bg-gray-800 text-[#828382] dark:text-gray-400 rounded"
                            >
                              {tag}
                            </span>
                          ))}
                          {chart.tags.length > 3 && (
                            <span className="text-xs px-2 py-0.5 text-[#828382] dark:text-gray-500">
                              +{chart.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-[#E8EBE9] dark:border-[#3a3c3b] flex justify-between items-center">
              <span className="text-[#828382] dark:text-gray-500 text-sm">
                {t('analytics.widgetsActive', { active: selectedWidgetIds.length, total: allCharts.length })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-bloom-green hover:bg-bloom-green-hover text-white text-sm rounded transition-colors font-medium"
                >
                  {t('analytics.save')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddWidgetPopup;
